import { Column, DataTable } from '@creditornot/cb-data-table';
import { Download } from '@creditornot/cb-icons';
import { Empty } from '@creditornot/cb-placeholders';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { FC, useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Brand } from 'apis/enterprise/types';
import { getAllBrandPayouts, getCombinedBrandPayoutReport } from 'apis/merchant-payout-service/api';
import { AllBrandPayouts, BrandPayout } from 'apis/merchant-payout-service/types';
import PageHeader from 'common/components/PageHeader';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { downloadFileFromAxiosResponse } from 'common/utils/download';
import Loading from 'core/Loading';
import useTimezone from 'locale/useTimezone';
import { useBrandContext } from 'modules/brand/context/BrandContext';

import LocalizedMonetaryAmount from './LocalizedMonetaryAmount';

const EmptyMessage = styled(Empty)`
  margin-block-start: 100px;
`;

const DownloadIcon = styled(Download)`
  width: 16px;
  cursor: pointer;
`;

const DisabledDownloadIcon = styled(Download)`
  width: 16px;
  opacity: 0.5;
  pointer-events: none;
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

const intervalTranslations: { [key: string]: string } = {
  custom: 'payout-reports.custom',
  hexa_monthly: 'payout-reports.hexa-monthly',
  monthly: 'payout-reports.monthly',
  semi_monthly: 'payout-reports.semi-monthly',
  semi_weekly: 'payout-reports.semi-weekly',
  weekly: 'payout-reports.weekly',
};

const BrandPayouts: FC = () => {
  const intl = useIntl();
  const { brand } = useBrandContext();
  const brandId = brand?.id || '';

  const { data: brandPayoutsData, isLoading } = useQuery<AllBrandPayouts>(
    ['brandPayoutsData', brandId],
    () => getAllBrandPayouts(brandId),
    { enabled: !!brand?.id },
  );

  if (isLoading || !brandPayoutsData) {
    return <Loading />;
  }

  return (
    <Root>
      <PageHeader title={intl.formatMessage({ id: 'navigation.payout-reports' })} />
      {brandPayoutsData.payouts.length === 0 && (
        <EmptyMessage title={intl.formatMessage({ id: 'purchases.messages.no-items' })} />
      )}
      {brand && brandPayoutsData.payouts.length !== 0 && (
        <BrandPayoutsDataTable brand={brand} payouts={brandPayoutsData.payouts} currency={brandPayoutsData.currency} />
      )}
    </Root>
  );
};

type BrandPayoutsDataTableProps = {
  brand: Brand;
  payouts: BrandPayout[];
  currency: Currency;
};

const BrandPayoutsDataTable: FC<BrandPayoutsDataTableProps> = ({ brand, payouts, currency }) => {
  const intl = useIntl();
  const timezone = useTimezone({ type: 'brand', brand });
  const [payoutsBeingDownloaded, setPayoutsBeingDownloaded] = useState<string[]>([]);
  const t = (id: string) => intl.formatMessage({ id });

  const downloadReport = (payout: BrandPayout) => {
    setPayoutsBeingDownloaded(_ => [..._, payout.id]);

    getCombinedBrandPayoutReport(payout.brandId, payout.id)
      .then(downloadFileFromAxiosResponse)
      .catch(() => toast('Download failed'))
      .finally(() => setPayoutsBeingDownloaded(current => current.filter(_ => _ !== payout.id)));
  };
  const columns: Column<BrandPayout, Record<string, unknown>>[] = [
    {
      id: '1',
      name: t('payout-reports.report'),
      render: ({ record: payout }) => (
        <>
          {t('payout-reports.report')} {t(intervalTranslations[payout.interval.type])} {payout.interval.number}
        </>
      ),
    },
    {
      id: '2',
      name: t('special-hours.period'),
      render: ({ record: payout }) => {
        const startDate = DateTime.fromISO(payout.interval.startDate).setZone(timezone);
        const endDate = DateTime.fromISO(payout.interval.endDate).setZone(timezone);
        return (
          <>
            {startDate.toFormat('MMM d, y')} - {endDate.toFormat('MMM d, y')}
          </>
        );
      },
    },
    {
      id: '3',
      name: t('payout-reports.amount'),
      render: ({ record: payout }) => {
        return (
          <LocalizedMonetaryAmount
            amount={convertMonetaryIntToDecimal(payout.payoutAmount, currency)}
            currency={currency}
          />
        );
      },
    },
    {
      id: '4',
      name: '',
      alignContent: 'center',
      render: ({ record: payout }) => {
        const isPayoutBeingDownloaded = payoutsBeingDownloaded.includes(payout.id);
        const isDisabled = payout.payoutAmount === 0 || !payout.archived;

        return (
          <>
            {isPayoutBeingDownloaded && <Spinner size="small" color="blue" />}
            {!isPayoutBeingDownloaded && isDisabled && <DisabledDownloadIcon />}
            {!isPayoutBeingDownloaded && !isDisabled && <DownloadIcon onClick={() => downloadReport(payout)} />}
          </>
        );
      },
    },
  ];

  return (
    <DataTable<BrandPayout>
      dataSource={payouts}
      columns={columns}
      getRowKey={payout => payout.id}
      emptyMessage={{
        title: t('state.error'),
        description: t('errors.unknown-error-retry-contact'),
      }}
      horizontalPadding="wide"
    />
  );
};

export default BrandPayouts;
